import React, { useMemo } from "react";

import { exists, getNestedAggregationBuckets } from "@examspark/common";
import { useQueriesGetUnits } from "@examspark/common-app";
import { Text } from "@examspark/common-ui";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";

import { Filter, FilterData } from "../Browse";
import { useSearch, useSearchFn } from "../useSearch";

import FilterCheckboxList from "./FilterCheckboxList";

const FilterUnitsByTag = (props: {
  onChange: (values: string[]) => void;
  filters: FilterData;
  useSearchFn?: useSearchFn;
}) => {
  const filter = useMemo(() => {
    const { tags, ...filter } = props.filters;
    const { "unit-id": _, ...otherTags } = tags ?? {};
    return { tags: otherTags, ...filter };
  }, [props.filters]);

  const searchFn = props.useSearchFn ?? useSearch;

  const debouncedFilter = useDebounce(filter, 500);

  const { data } = searchFn({
    queryKey: ["aggs", "unitByTag", debouncedFilter],
    searchLabel: "SearchAggsUnitByTag",
    limit: 0,
    filter: debouncedFilter,
  });

  const buckets = getNestedAggregationBuckets(
    data?.aggregations ?? {},
    "tagsUnit",
  );

  const unitsQuery = useQueriesGetUnits(
    buckets.map((b) => ({ id: b.key, sk: "unit#v0" })),
  );

  if (buckets.length === 0) return null;

  return (
    <Filter>
      <Text.H2>Units / Modules</Text.H2>
      <FilterCheckboxList>
        {unitsQuery
          ?.map((query) => query.data)
          .filter(exists)
          .sort((a, b) => a.name?.localeCompare(b?.name ?? "") ?? 0)
          .map((unit) => {
            const bucket = buckets.find((b) => b.key === unit.id);

            return (
              <FilterCheckboxList.Item
                key={unit.id}
                checked={
                  props.filters.tags?.["unit-id"]?.includes(unit.id) ?? false
                }
                count={bucket?.doc_count}
                onClick={() => {
                  props.onChange(
                    props.filters.tags?.["unit-id"]?.includes(unit.id)
                      ? props.filters.tags["unit-id"]?.filter(
                          (id) => id !== unit.id,
                        )
                      : [...(props.filters.tags?.["unit-id"] ?? []), unit.id],
                  );
                }}
              >
                {unit.name}
              </FilterCheckboxList.Item>
            );
          })}
      </FilterCheckboxList>
    </Filter>
  );
};

export default FilterUnitsByTag;
